import React from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Pause, Play, Volume2, VolumeX, SkipForward, X } from "lucide-react";
import { useShowreel } from "@/components/showreel/useShowreel";
import { TID } from "@/lib/testIds";

export const PRESENTER_IMG = "/showreel-presenter.webp";

/** Cinematic, scene-by-scene film about UTSAVA Genesis: the creator stands as
 * the on-screen presenter while each scene's headline and narration play. */
export function ShowreelPlayer({ muted, setMuted, onClose }) {
    const { scenes, scene, index, playing, setPlaying, advance, goTo } = useShowreel({ active: true, muted });
    if (!scene) return null;

    return (
        <div data-testid={TID.showreelStage} className="relative w-full h-full overflow-hidden bg-[var(--bg-base)]">
            {/* Cinematic backdrop */}
            <div className="absolute inset-0 grid-bg opacity-[0.16] pointer-events-none" />
            <div className="absolute inset-0 pointer-events-none" style={{
                background: "radial-gradient(circle at 22% 55%, rgb(var(--accent-2-rgb) / 0.24), transparent 42%), radial-gradient(circle at 78% 62%, rgb(var(--accent-3-rgb) / 0.18), transparent 45%)",
            }} />

            {/* Presenter — full-height figure on the left, feathered into the dark canvas */}
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
                <motion.img
                    key={`presenter-${index}`}
                    data-testid={TID.showreelPresenter}
                    src={PRESENTER_IMG}
                    alt="Utsav Mukherjee presenting UTSAVA Genesis"
                    initial={{ scale: 1.0, opacity: 0 }}
                    animate={{ scale: 1.07, opacity: 1 }}
                    transition={{ scale: { duration: 14, ease: "linear" }, opacity: { duration: 1.2 } }}
                    className="absolute left-0 top-0 h-full w-auto object-contain object-left-top"
                />
                <div className="absolute inset-0" style={{
                    background: "linear-gradient(90deg, rgba(5,5,10,0) 0%, rgba(5,5,10,0.35) 14%, rgba(5,5,10,0.92) 34%, #05050A 52%)",
                }} />
                <div className="absolute inset-x-0 bottom-0 h-1/4" style={{ background: "linear-gradient(0deg, #05050A, transparent)" }} />
            </div>
            <div className="absolute inset-0 scanline pointer-events-none opacity-50" />

            {/* Scene copy */}
            <div className="relative h-full flex items-center justify-end px-8 md:px-16 pb-24">
                <div className="w-full md:w-[64%] max-w-3xl">
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={scene.id}
                            initial={{ opacity: 0, y: 26 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -18 }}
                            transition={{ duration: 0.55, ease: "easeOut" }}
                        >
                            <div className="label-eyebrow mb-3">{scene.eyebrow}</div>
                            <h2 data-testid={TID.showreelHeadline} className="font-display font-light text-3xl md:text-5xl leading-[1.05] text-white tracking-tight">
                                {scene.headline}
                            </h2>
                            <p className="mt-5 font-mono text-[11px] md:text-xs uppercase tracking-[0.18em] text-cyan-300/80">
                                {scene.sub}
                            </p>
                            <p data-testid={TID.showreelCaption} className="mt-7 text-sm md:text-base text-slate-300/90 leading-relaxed">
                                {scene.narration}
                            </p>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>

            {/* Controls + progress */}
            <div className="absolute inset-x-0 bottom-0 px-8 md:px-14 pb-7 pt-10 bg-gradient-to-t from-[#05050A] via-[#05050A]/85 to-transparent">
                <div className="flex items-center gap-1.5 mb-4">
                    {scenes.map((s, i) => (
                        <button
                            key={s.id}
                            data-testid={TID.showreelSceneDot(s.id)}
                            onClick={() => goTo(i)}
                            aria-label={`Scene ${i + 1}: ${s.headline}`}
                            className={`h-[3px] flex-1 rounded-full transition-colors ${i === index ? "bg-cyan-400" : i < index ? "bg-cyan-500/40" : "bg-white/12"}`}
                        />
                    ))}
                </div>
                <div className="flex items-center justify-between gap-4 flex-wrap">
                    <div className="flex items-center gap-4">
                        <button data-testid={TID.showreelPlayToggle} onClick={() => setPlaying(!playing)} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-slate-300 hover:text-cyan-300 transition-colors">
                            {playing ? <Pause size={13} /> : <Play size={13} />} {playing ? "Pause" : "Play"}
                        </button>
                        <button data-testid={TID.showreelSoundToggle} onClick={() => setMuted(!muted)} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-slate-300 hover:text-cyan-300 transition-colors">
                            {muted ? <VolumeX size={13} /> : <Volume2 size={13} />} {muted ? "Enable narration" : "Mute"}
                        </button>
                        <button data-testid={TID.showreelSkipBtn} onClick={advance} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-slate-300 hover:text-cyan-300 transition-colors">
                            <SkipForward size={13} /> Next
                        </button>
                    </div>
                    <div className="flex items-center gap-5">
                        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">
                            Scene {index + 1} / {scenes.length} · um-alphabydesign
                        </span>
                        {onClose && (
                            <button data-testid={TID.showreelCloseBtn} onClick={onClose} className="inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-wider text-slate-400 hover:text-rose-300 transition-colors">
                                <X size={13} /> Close
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
